import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useAnnouncements } from '@/hooks/use-announcements';
import { Trash2, Send } from 'lucide-react';
import { useUserRole } from '@/hooks/use-user-role';

export const AnnouncementManager = () => {
  const { isSuperAdmin, isLoading: roleLoading } = useUserRole();
  const { announcements, createAnnouncement, deleteAnnouncement } = useAnnouncements();
  const [newMessage, setNewMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (roleLoading) return null;
  if (!isSuperAdmin) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    setIsSubmitting(true);
    await createAnnouncement(newMessage);
    setNewMessage('');
    setIsSubmitting(false);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Announcement Manager</CardTitle>
        <CardDescription>
          Create and manage announcements that appear in the ticker bar
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <form onSubmit={handleSubmit} className="flex gap-2">
          <Input
            placeholder="Enter announcement message..."
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            disabled={isSubmitting}
          />
          <Button type="submit" disabled={isSubmitting || !newMessage.trim()}>
            <Send className="h-4 w-4 mr-2" />
            Send
          </Button>
        </form>

        <div className="space-y-2">
          <h3 className="text-sm font-medium">Active Announcements</h3>
          {announcements.length === 0 ? (
            <p className="text-sm text-muted-foreground">No active announcements</p>
          ) : (
            announcements.map((announcement) => (
              <div
                key={announcement.id}
                className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
              >
                <p className="text-sm flex-1">{announcement.message}</p>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => deleteAnnouncement(announcement.id)}
                >
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </div>
            ))
          )}
        </div>
      </CardContent>
    </Card>
  );
};
